import React, { useState, useEffect } from 'react';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import EditableSection from 'components/v2/EditableSection';
import Label from 'components/v2/forms/Label';
import Select from 'components/v2/select';
import TextField from 'components/v2/forms/TextField';
import { useTranslation } from 'react-i18next';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import { titleize } from 'underscore.string';
import { formatMoney } from 'accounting';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import {
  checkPermissonForEditShipperBillingModule,
  payloadMappingOnlyForChanges,
} from './utils';

const MAX_CREDIT_LIMIT = 999999999.99;

const PaymentTermsSummary = ({ fields, overwritePaymentTermsSlugs }) => {
  const { t } = useTranslation();
  const drayageSlug = overwritePaymentTermsSlugs
    ? 'FIRST_MILE_MID_MILE'
    : 'DRAYAGE';
  const shorthaulSlug = overwritePaymentTermsSlugs ? 'LAST_MILE' : 'SHORTHAUL';
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('CREDIT_LIMIT')} className="">
          {fields.credit_limit
            ? formatMoney(
                fields.credit_limit,
                getCurrencySymbol(DEFAULT_CURRENCY),
              )
            : t('NOT_SET')}
        </DescriptionListItem>
      </div>
      <hr />
      <p className="text-sm text-gray-700 font-medium mb-2">{t(drayageSlug)}</p>
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('PAYMENT_METHOD')} className="">
          {fields.drayage_payment_method
            ? titleize(fields.drayage_payment_method)
            : t('NOT_SET')}
        </DescriptionListItem>
        <DescriptionListItem label={t('PAYMENT_TERMS')} className="">
          {fields.drayage_terms ? fields.drayage_terms : t('NOT_SET')}
        </DescriptionListItem>
      </div>
      <hr className="my-4" />
      <p className="text-sm text-gray-700 font-medium mb-2">
        {t(shorthaulSlug)}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <DescriptionListItem label={t('PAYMENT_METHOD')} className="">
          {fields.shorthaul_payment_method
            ? titleize(fields.shorthaul_payment_method)
            : t('NOT_SET')}
        </DescriptionListItem>
        <DescriptionListItem label={t('PAYMENT_TERMS')} className="">
          {fields.shorthaul_terms ? fields.shorthaul_terms : t('NOT_SET')}
        </DescriptionListItem>
      </div>
    </>
  );
};

const PaymentTermsForm = ({
  onChange,
  onSaveShipper,
  onSaveSuccess,
  onSaveFail,
  saving,
  fields = {},
}) => {
  const { t } = useTranslation();

  const setErrors = (field, value) => {
    onChange('error', { ...fields.error, [field]: value });
  };
  const paymentMethodOptions = [
    {
      value: 'manual',
      name: 'Manual',
      localizationSlug: 'MANUAL',
    },
    {
      value: 'automated',
      name: 'Automated',
      localizationSlug: 'AUTOMATED',
    },
  ];
  const getPaymentOptions = (flag) => {
    if (flag) {
      return paymentMethodOptions;
    }
    return paymentMethodOptions.filter((o) => o.value !== 'automated');
  };
  useEffect(() => {
    if (
      fields.drayage_terms > 120 ||
      fields.drayage_terms < 0 ||
      !fields.drayage_terms
    ) {
      setErrors(
        'drayage_terms',
        t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 0, num2: 120 }),
      );
    } else if (fields?.error?.drayage_terms) {
      setErrors('drayage_terms', '');
    }
  }, [fields.drayage_terms]);

  const saveForm = async () => {
    let isValid = false;
    if (
      (!fields?.error?.drayage_terms && !fields?.error?.shorthaul_terms) ||
      !fields?.error?.credit_limit
    ) {
      isValid = true;
    }
    try {
      if (isValid) {
        await onSaveShipper();
        onSaveSuccess(fields);
      }
    } catch (e) {
      onSaveFail();
    }
  };

  useEffect(() => {
    if (
      fields.shorthaul_terms > 120 ||
      fields.shorthaul_terms < 0 ||
      !fields.shorthaul_terms
    ) {
      setErrors(
        'shorthaul_terms',
        t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 0, num2: 120 }),
      );
    } else if (fields?.error?.shorthaul_terms) {
      setErrors('shorthaul_terms', '');
    }
  }, [fields.shorthaul_terms]);

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('CREDIT_LIMIT')}</Label>
          <TextField
            className="mt-1"
            type="number"
            min={0}
            max={MAX_CREDIT_LIMIT}
            value={fields.credit_limit}
            onChange={(val) => onChange('credit_limit', val)}
          />
          {fields?.error?.credit_limit && (
            <ErrorLabel className="mt-1">
              {fields?.error?.credit_limit}
            </ErrorLabel>
          )}
        </div>
      </div>
      <hr />
      <p className="text-sm text-gray-700 font-medium mb-2">{t('DRAYAGE')}</p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('PAYMENT_METHOD')}</Label>
          <Select
            labelClassName="m-0"
            items={getPaymentOptions(fields.hasCreditCardOnFile)}
            valueKey="value"
            textKey="localizationSlug"
            selected={fields.drayage_payment_method}
            onSelect={({ name }) => onChange('drayage_payment_method', name)}
          />
        </div>
        <div>
          <Label>{t('PAYMENT_TERMS')}</Label>
          <TextField
            className="mt-1"
            type="number"
            value={fields.drayage_terms}
            onChange={(val) => onChange('drayage_terms', val)}
          />
          {fields?.error?.drayage_terms && (
            <ErrorLabel className="mt-1">
              {fields?.error?.drayage_terms}
            </ErrorLabel>
          )}
        </div>
      </div>
      <hr className="my-4" />
      <p className="text-sm text-gray-700 font-medium mb-2">{t('SHORTHAUL')}</p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t('PAYMENT_METHOD')}</Label>
          <Select
            labelClassName="m-0"
            items={getPaymentOptions(fields.hasCreditCardOnFile)}
            valueKey="value"
            textKey="localizationSlug"
            selected={fields.shorthaul_payment_method}
            onSelect={({ name }) => {
              onChange('shorthaul_payment_method', name);
            }}
          />
        </div>
        <div>
          <Label>{t('PAYMENT_TERMS')}</Label>
          <TextField
            className="mt-1"
            type="number"
            value={fields.shorthaul_terms}
            onChange={(val) => onChange('shorthaul_terms', val)}
          />
          {fields?.error?.shorthaul_terms && (
            <ErrorLabel className="mt-1">
              {fields?.error?.shorthaul_terms}
            </ErrorLabel>
          )}
        </div>
      </div>
    </>
  );
};

const PaymentTerms = ({
  onUpdateShipper,
  className,
  data,
  overwritePaymentTermsSlugs = false,
}) => {
  const { t } = useTranslation();
  const [initialData, setInitialData] = useState(data);
  const [paymentTermsData, setPaymentTermsData] = useState({
    ...data,
    error: {
      drayage_terms: '',
      shorthaul_terms: '',
    },
  });

  const onChange = (field, value) => {
    let actualValue = value;
    if (
      (field === 'drayage_payment_method' ||
        field === 'shorthaul_payment_method') &&
      value
    ) {
      actualValue = value.toLowerCase();
    }
    if (field === 'credit_limit') {
      if (value.includes('-')) {
        actualValue = Math.abs(value);
      }
      if (value > MAX_CREDIT_LIMIT) {
        actualValue = MAX_CREDIT_LIMIT;
      }
    }
    setPaymentTermsData((prev) => ({ ...prev, [field]: actualValue }));
  };

  const getInitData = () => {
    if (data) {
      setPaymentTermsData((prev) => {
        return { ...prev, ...data };
      });
      setInitialData(data);
    }
  };

  const handleSave = async () => {
    const payload = payloadMappingOnlyForChanges(initialData, paymentTermsData);
    if (Object.keys(payload).length) {
      await onUpdateShipper(payload);
    }
  };

  const handleAfterSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setInitialData((prev) => ({ ...prev, ...updatedData }));
      onSaveSuccess();
    };
  };

  useEffect(() => {
    getInitData();
  }, [data]);
  return (
    <EditableSection
      title={t('CREDIT_LIMIT_AND_PAYMENT_TERMS')}
      canEdit={checkPermissonForEditShipperBillingModule()}
      className={className}
    >
      {(editMode, saving, onSaveSuccess, onSaveFail) =>
        editMode ? (
          <PaymentTermsForm
            onChange={onChange}
            fields={paymentTermsData}
            saving={saving}
            onSaveSuccess={handleAfterSave(onSaveSuccess)}
            onSaveFail={() => onSaveFail()}
            onSaveShipper={() => handleSave()}
          />
        ) : (
          <PaymentTermsSummary
            fields={initialData}
            overwritePaymentTermsSlugs={overwritePaymentTermsSlugs}
          />
        )
      }
    </EditableSection>
  );
};

export default PaymentTerms;
